import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

// Mirrors the API's emergency_contacts shape after the multi-scope rework.
// scope drives WHICH alarms a contact gets notified about.
// notify_after_seconds is the escalation tier (0 = immediate; >0 = only fires if
// the alarm is still unacknowledged after that many seconds).
// recipient_user_id, when set, links to a user in our system whose
// notification_preferences govern channel + active-hour selection at dispatch time.

export type ContactScope = 'user' | 'team' | 'site' | 'organisation';

export interface EmergencyContactDto {
  id: string;
  organisation_id: string;
  scope: ContactScope;
  user_id: string | null;
  team_id: string | null;
  site_id: string | null;
  recipient_user_id: string | null;
  name: string;
  role: string | null;
  phone_e164: string | null;
  email: string | null;
  is_primary: boolean;
  notify_on_duress: boolean;
  notify_on_overdue: boolean;
  priority: number;
  notify_after_seconds: number;
  created_at: string;
}

export interface OrgEmergencyContactDto extends EmergencyContactDto {
  worker_display_name: string | null;
  worker_email: string | null;
  team_name: string | null;
  site_name: string | null;
}

export interface ScopedContactInput {
  name: string;
  role?: string | null;
  phone_e164?: string | null;
  email?: string | null;
  recipient_user_id?: string | null;
  is_primary?: boolean;
  notify_on_duress?: boolean;
  notify_on_overdue?: boolean;
  priority?: number;
  notify_after_seconds?: number;
}

export interface UpdateContactInput extends Partial<ScopedContactInput> {
  id: string;
}

// -------- Listings --------

export function useUserEmergencyContacts(userId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['emergency-contacts', 'user', userId],
    queryFn: () => api<{ contacts: EmergencyContactDto[] }>(`/users/${userId}/emergency-contacts`),
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

export function useTeamEmergencyContacts(teamId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['emergency-contacts', 'team', teamId],
    queryFn: () => api<{ contacts: EmergencyContactDto[] }>(`/teams/${teamId}/emergency-contacts`),
    enabled: Boolean(teamId),
    staleTime: 30_000,
  });
}

export function useSiteEmergencyContacts(siteId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['emergency-contacts', 'site', siteId],
    queryFn: () => api<{ contacts: EmergencyContactDto[] }>(`/sites/${siteId}/emergency-contacts`),
    enabled: Boolean(siteId),
    staleTime: 30_000,
  });
}

export function useOrganisationEmergencyContacts() {
  const api = useApi();
  return useQuery({
    queryKey: ['emergency-contacts', 'organisation'],
    queryFn: () => api<{ contacts: EmergencyContactDto[] }>('/organisations/me/emergency-contacts'),
    staleTime: 30_000,
  });
}

// Whole-org listing for the broadcasts composer / admin overview page.
export function useOrgEmergencyContacts() {
  const api = useApi();
  return useQuery({
    queryKey: ['emergency-contacts', 'org'],
    queryFn: () => api<{ contacts: OrgEmergencyContactDto[] }>('/emergency-contacts'),
    staleTime: 30_000,
  });
}

// -------- Mutations (scope-targeted POSTs + unified PATCH/DELETE) --------

interface CreateUserContact extends ScopedContactInput {
  user_id: string;
}
export function useCreateUserContact() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ user_id, ...body }: CreateUserContact) =>
      api<{ contact: EmergencyContactDto }>(`/users/${user_id}/emergency-contacts`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emergency-contacts'] }),
  });
}

interface CreateTeamContact extends ScopedContactInput {
  team_id: string;
}
export function useCreateTeamContact() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ team_id, ...body }: CreateTeamContact) =>
      api<{ contact: EmergencyContactDto }>(`/teams/${team_id}/emergency-contacts`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emergency-contacts'] }),
  });
}

interface CreateSiteContact extends ScopedContactInput {
  site_id: string;
}
export function useCreateSiteContact() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ site_id, ...body }: CreateSiteContact) =>
      api<{ contact: EmergencyContactDto }>(`/sites/${site_id}/emergency-contacts`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emergency-contacts'] }),
  });
}

export function useCreateOrganisationContact() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ScopedContactInput) =>
      api<{ contact: EmergencyContactDto }>('/organisations/me/emergency-contacts', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emergency-contacts'] }),
  });
}

// Backwards-compat alias for the previous user-scoped useCreateContact.
export const useCreateContact = useCreateUserContact;

export function useUpdateContact() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateContactInput) =>
      api<{ contact: EmergencyContactDto }>(`/emergency-contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emergency-contacts'] }),
  });
}

export function useDeleteContact() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/emergency-contacts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emergency-contacts'] }),
  });
}
